import React, { useState, useEffect, useCallback } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    List,
    ListItem,
    ListItemText,
    Divider,
    CircularProgress,
    IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { format } from "date-fns";
import API from "../../utils/Axios.utils";
import { Notification } from "../../utils/Notification";
import useAuth from "../../hooks/useAuth";

// Style constants to prevent recreation on every render
const dialogTitleSx = { m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const dialogContentSx = { minHeight: '300px', display: 'flex', flexDirection: 'column' };
const loadingBoxSx = { display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 };
const notesContainerSx = { flexGrow: 1, overflowY: 'auto', mb: 2 };
const emptyStateSx = { textAlign: 'center', mt: 4, color: 'text.secondary' };
const noteHeaderBoxSx = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' };
const noteDateSx = { fontWeight: 'bold', color: '#666', fontSize: '0.85rem' };
const noteAuthorSx = { fontWeight: 'bold', color: 'primary.main', fontSize: '0.85rem' };
const noteSecondaryTypographyProps = { style: { whiteSpace: 'pre-wrap', color: '#333', marginTop: '4px', fontSize: '1rem' } };
const textFieldContainerSx = { mt: 'auto' };
const dialogActionsSx = { p: 2 };

const JobNotesDialog = ({ open, onClose, jobId, jobCode }) => {
    const { token } = useAuth();
    const [notes, setNotes] = useState([]);
    const [newNote, setNewNote] = useState("");
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [alert, setAlert] = useState(false);
    const [alertContent, setAlertContent] = useState("");
    const [alertType, setAlertType] = useState("");

    const fetchNotes = useCallback(async () => {
        setLoading(true);
        try {
            // const token = JSON.parse(localStorage.getItem("userLoggedIn")).token;
            const config = { headers: { Authorization: `Bearer ${token}` } };
            // const res = await API.get(`/api/job-notes/${jobId}`, config);
            const res = await API.get(`/api/job/notes/${jobId}`, config);
            // After refactoring and global interceptor, res.data is already the unwrapped data object
            const notesData = res.data.notes || res.data;
            setNotes(Array.isArray(notesData) ? notesData : []);
        } catch (error) {
            console.error("Error fetching notes:", error);
            setAlert(true);
            setAlertContent("Failed to fetch notes");
            setAlertType("error");
        } finally {
            setLoading(false);
        }
    }, [token, jobId]);

    useEffect(() => {
        if (open && jobId) {
            fetchNotes();
        }
    }, [open, jobId, fetchNotes]);

    const handleNoteChange = useCallback((e) => {
        setNewNote(e.target.value);
    }, []);

    const handleAlertClose = useCallback(() => {
        setAlert(false);
    }, []);

    const handleSubmit = useCallback(async () => {
        if (!newNote.trim()) return;

        setSubmitting(true);
        try {
            // const token = JSON.parse(localStorage.getItem("userLoggedIn")).token;
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const res = await API.post("/api/job/notes", {
                job_id: jobId,
                note: newNote
            }, config);

            // If we are here, it means the request was successful (2xx)
            setNewNote("");
            fetchNotes(); // Refresh list
            setAlert(true);
            setAlertContent("Note added successfully");
            setAlertType("success");
        } catch (error) {
            console.error("Error adding note:", error);
            setAlert(true);
            setAlertContent("Failed to add note");
            setAlertType("error");
        } finally {
            setSubmitting(false);
        }
    }, [newNote, token, jobId, fetchNotes]);

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle sx={dialogTitleSx}>
                    <Typography variant="h6">Notes for Job: {jobCode}</Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={dialogContentSx}>
                    {loading ? (
                        <Box sx={loadingBoxSx}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Box sx={notesContainerSx}>
                            {notes.length === 0 ? (
                                <Typography sx={emptyStateSx}>
                                    No notes yet. Be the first to add one!
                                </Typography>
                            ) : (
                                <List>
                                    {notes.map((note, index) => (
                                        <React.Fragment key={note.id}>
                                            <ListItem alignItems="flex-start">
                                                <ListItemText
                                                    primary={
                                                        <Box sx={noteHeaderBoxSx}>
                                                            <Typography variant="caption" sx={noteDateSx}>
                                                                {note.created_at ? format(new Date(note.created_at), "dd MMM yyyy, hh:mm a") : ''}
                                                            </Typography>
                                                            {note.Admin && (
                                                                <Typography variant="caption" sx={noteAuthorSx}>
                                                                    By: {note.Admin.first_name} {note.Admin.last_name}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    }
                                                    secondary={note.note}
                                                    secondaryTypographyProps={noteSecondaryTypographyProps}
                                                />
                                            </ListItem>
                                            {index < notes.length - 1 && <Divider component="li" />}
                                        </React.Fragment>
                                    ))}
                                </List>
                            )}
                        </Box>
                    )}
                    <Box sx={textFieldContainerSx}>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            placeholder="Type your note here..."
                            value={newNote}
                            onChange={handleNoteChange}
                            disabled={submitting}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={dialogActionsSx}>
                    <Button onClick={onClose} color="inherit">Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="primary"
                        disabled={!newNote.trim() || submitting}
                    >
                        {submitting ? "Adding..." : "Add Note"}
                    </Button>
                </DialogActions>
            </Dialog>
            <Notification
                message={alertContent}
                open={alert}
                severity={alertType}
                onClose={handleAlertClose}
            />
        </>
    );
};

export default JobNotesDialog;
